import { Link } from 'react-router-dom';
import {
  MoreHorizontal, ExternalLink, Pencil, Copy, FileText,
  AlertTriangle, AlertCircle, MapPin, MapPinOff, ImageOff,
  CalendarOff, TagIcon, Bug, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { CoverThumb } from '@/components/media/CoverThumb';
import type { AdminProducerRow } from '@/hooks/useAdminProducersWithMaturity';
import { useToast } from '@/hooks/use-toast';

interface Props {
  producer: AdminProducerRow;
  coverUrl320?: string | null;
}

// ── Issue classification ──────────────────────────────────────────

interface Issue {
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  blocking: boolean;
  link: string;
  cta: string;
}

function classifyIssues(p: AdminProducerRow): Issue[] {
  const issues: Issue[] = [];
  if (!p.has_place) {
    issues.push({
      label: 'Saknar platskoppling',
      shortLabel: 'plats',
      icon: <MapPinOff className="h-3 w-3" />,
      blocking: true,
      link: `/app/admin/producers/${p.id}?focus=place`,
      cta: 'Koppla plats',
    });
  }
  if (p.categories.length === 0) {
    issues.push({
      label: 'Saknar kategori',
      shortLabel: 'kategori',
      icon: <TagIcon className="h-3 w-3" />,
      blocking: true,
      link: `/app/admin/producers/${p.id}?focus=categories`,
      cta: 'Lägg till kategori',
    });
  }
  if (!p.has_cover_image) {
    issues.push({
      label: 'Saknar omslagsbild',
      shortLabel: 'bild',
      icon: <ImageOff className="h-3 w-3" />,
      blocking: true,
      link: `/app/admin/producers/${p.id}?focus=media`,
      cta: 'Lägg till bild',
    });
  }
  if (!p.has_activity) {
    issues.push({
      label: 'Saknar aktivitet',
      shortLabel: 'aktivitet',
      icon: <CalendarOff className="h-3 w-3" />,
      blocking: false,
      link: `/app/admin/producers/${p.id}`,
      cta: 'Skapa aktivitet',
    });
  }
  return issues;
}

// ── Primary status derivation ─────────────────────────────────────

interface PrimaryStatus {
  text: string;
  tone: 'destructive' | 'warning' | 'success' | 'info' | 'muted';
}

function derivePrimaryStatus(p: AdminProducerRow, issues: Issue[]): PrimaryStatus {
  const blockers = issues.filter((i) => i.blocking);
  const soft = issues.filter((i) => !i.blocking);

  if (blockers.length > 0) {
    const missing = blockers.map((b) => b.shortLabel).join(', ');
    return {
      text: `Behöver åtgärd: saknar ${missing}`,
      tone: p.status === 'public' ? 'destructive' : 'warning',
    };
  }

  if (p.status === 'draft' && p.maturity_score >= 5) {
    return { text: 'Redo att publiceras', tone: 'success' };
  }

  if (p.status === 'public' && soft.length > 0) {
    return { text: 'Publicerad – bör kompletteras', tone: 'info' };
  }

  if (p.status === 'public') {
    return { text: 'Publicerad och komplett', tone: 'muted' };
  }

  return { text: 'Utkast – pågår', tone: 'muted' };
}

// ── Primary CTA derivation ────────────────────────────────────────

function derivePrimaryCta(p: AdminProducerRow, issues: Issue[]): { label: string; link: string } {
  // First blocker takes priority
  if (issues.length > 0) {
    return { label: issues[0].cta, link: issues[0].link };
  }
  if (p.status === 'draft' && p.maturity_score >= 5) {
    return { label: 'Publicera', link: `/app/admin/producers/${p.id}` };
  }
  return { label: 'Öppna', link: `/app/admin/producers/${p.id}` };
}

// ── Missing items for progress ────────────────────────────────────

function getMissingLabels(p: AdminProducerRow): string[] {
  const missing: string[] = [];
  if (!p.has_place) missing.push('plats');
  if (p.categories.length === 0) missing.push('kategori');
  if (!p.has_cover_image) missing.push('bild');
  if (!p.has_any_image) missing.push('galleri');
  if (!p.has_activity) missing.push('aktivitet');
  return missing;
}

// ── Tone classes ──────────────────────────────────────────────────

const toneClasses: Record<PrimaryStatus['tone'], string> = {
  destructive: 'bg-destructive/10 text-destructive',
  warning: 'bg-accent/60 text-accent-foreground',
  success: 'bg-primary/10 text-primary',
  info: 'bg-secondary text-secondary-foreground',
  muted: 'bg-muted text-muted-foreground',
};

const toneIcon: Record<PrimaryStatus['tone'], React.ReactNode> = {
  destructive: <AlertCircle className="h-3 w-3 shrink-0" />,
  warning: <AlertTriangle className="h-3 w-3 shrink-0" />,
  success: <CheckCircle2 className="h-3 w-3 shrink-0" />,
  info: <AlertTriangle className="h-3 w-3 shrink-0" />,
  muted: null,
};

// ── Component ─────────────────────────────────────────────────────

export function ProducerMaturityListItem({ producer: p, coverUrl320 }: Props) {
  const { toast } = useToast();
  const displayCats = p.categories.slice(0, 2);
  const extraCount = p.categories.length - 2;
  const maturityPct = Math.round((p.maturity_score / 7) * 100);

  const issues = classifyIssues(p);
  const primaryStatus = derivePrimaryStatus(p, issues);
  const primaryCta = derivePrimaryCta(p, issues);
  const missingLabels = getMissingLabels(p);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/producer/${p.id}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Länk kopierad' });
  };

  return (
    <Link
      to={`/app/admin/producers/${p.id}`}
      className="group block rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors"
    >
      <div className="flex items-start gap-3 p-3 sm:p-4">
        {/* Thumbnail */}
        <div data-testid="producer-cover-thumb">
          <CoverThumb
            ownerType="producer"
            ownerId={p.id}
            alt={p.name}
            urlOverride={coverUrl320}
            size={48}
            className="sm:w-14 sm:h-14"
          />
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Row 1: Name + status badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-foreground truncate">
              {p.name}
            </span>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px] px-1.5 py-0 shrink-0',
                p.status === 'public'
                  ? 'border-primary/30 bg-primary/10 text-primary'
                  : 'border-muted-foreground/30 bg-muted text-muted-foreground'
              )}
            >
              {p.status === 'public' ? 'Public' : 'Utkast'}
            </Badge>
          </div>

          {/* Row 2: Primary status summary – the single most important line */}
          {primaryStatus.tone !== 'muted' && (
            <div
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2 py-0.5',
                toneClasses[primaryStatus.tone],
              )}
            >
              {toneIcon[primaryStatus.tone]}
              {primaryStatus.text}
            </div>
          )}

          {/* Row 3: Place + region */}
          <div className="flex items-center gap-1.5 text-xs">
            {p.has_place ? (
              <span className="text-muted-foreground truncate flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0 text-primary/60" />
                {[p.city, p.region].filter(Boolean).join(', ') || 'Kopplad plats'}
              </span>
            ) : (
              <span className="flex items-center gap-1 truncate text-muted-foreground">
                <MapPinOff className="h-3 w-3 shrink-0" />
                Saknar plats
              </span>
            )}
          </div>

          {/* Row 4: Categories */}
          {displayCats.length > 0 && (
            <div className="flex items-center gap-1">
              {displayCats.map((cat) => (
                <Badge key={cat} variant="secondary" className="text-[10px] px-1.5 py-0">
                  {cat}
                </Badge>
              ))}
              {extraCount > 0 && (
                <span className="text-[10px] text-muted-foreground">+{extraCount}</span>
              )}
            </div>
          )}

          {/* Row 5: Progress – human-readable */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground shrink-0">
              {p.maturity_score} av 7 klara
            </span>
            <Progress value={maturityPct} className="h-1.5 flex-1 max-w-[100px]" />
            {missingLabels.length > 0 && missingLabels.length <= 3 && (
              <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                Saknas: {missingLabels.join(', ')}
              </span>
            )}
          </div>

          {/* Row 6: Issue chips – blocking vs soft visually distinct */}
          {issues.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {issues.map((issue) => (
                <span
                  key={issue.label}
                  className={cn(
                    'inline-flex items-center gap-1 text-[10px] rounded-full border px-2 py-0.5',
                    issue.blocking
                      ? 'border-destructive/30 bg-destructive/5 text-destructive'
                      : 'border-border bg-accent/40 text-accent-foreground'
                  )}
                >
                  {issue.icon}
                  {issue.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions column */}
        <div className="shrink-0 flex items-center gap-1" onClick={(e) => e.preventDefault()}>
          {/* Contextual primary CTA */}
          <Button
            variant={primaryCta.label === 'Öppna' ? 'ghost' : 'outline'}
            size="sm"
            className={cn(
              'hidden sm:inline-flex h-8 text-xs gap-1',
              primaryCta.label !== 'Öppna' && 'border-primary/30 text-primary hover:bg-primary/5'
            )}
            asChild
          >
            <Link to={primaryCta.link}>
              {primaryCta.label}
              {primaryCta.label !== 'Öppna' && <ArrowRight className="h-3 w-3" />}
            </Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover">
              <DropdownMenuItem asChild>
                <Link to={`/app/admin/producers/${p.id}`}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Redigera
                </Link>
              </DropdownMenuItem>
              {p.status === 'public' && (
                <DropdownMenuItem asChild>
                  <Link to={`/producer/${p.id}`} target="_blank">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Förhandsvisa
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleCopyLink}>
                <Copy className="h-4 w-4 mr-2" />
                Kopiera länk
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {!p.has_place && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to={`/app/admin/producers/${p.id}?focus=place`}>
                      <MapPin className="h-4 w-4 mr-2" />
                      Koppla plats
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={`/admin/places/new?producer_id=${p.id}`}>
                      <MapPin className="h-4 w-4 mr-2" />
                      Skapa plats
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem asChild>
                <Link to={`/app/admin/system-logs?producerId=${p.id}`}>
                  <FileText className="h-4 w-4 mr-2" />
                  Visa logg
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/app/admin/system/host-debug?q=${p.id}`}>
                  <Bug className="h-4 w-4 mr-2" />
                  Host Debug
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Link>
  );
}
